import {
  FieldMessage,
  FormControl,
  Input,
  InputProps,
} from '@mr-yum/frontend-ui'
import React, { PropsWithChildren, useCallback } from 'react'
import { useController } from 'react-hook-form'

import { HookFormControlProps } from './shared'

export type NumberFieldProps = InputProps &
  HookFormControlProps & {
    toFixed?: number
  }

const INVALID_CHAR_REGEX = /[^\d\-.]+/
const INVALID_MULTIPLES_REGEX = /[.-]{2,}/

function parseValue(rawValue: string, toFixed?: number): string | number {
  const value = rawValue
    .replace(INVALID_CHAR_REGEX, '')
    .replace(INVALID_MULTIPLES_REGEX, (match) => match.substring(0, 1))

  const decimalPos = value.indexOf('.')

  if (decimalPos >= 0 && toFixed) {
    const leftSide = value.substring(0, decimalPos)
    const rightSide = value.substring(decimalPos + 1, decimalPos + 1 + toFixed)

    return leftSide + '.' + rightSide
  }

  return value
}

export const NumberField = ({
  name,
  label,
  disabled,
  caption,
  toFixed,
  ...props
}: PropsWithChildren<NumberFieldProps>) => {
  const { field, fieldState } = useController({ name })

  // ensure invalid characters are stripped on change
  const onChange = useCallback(
    (event: React.SyntheticEvent<HTMLInputElement>) => {
      field.onChange(parseValue(event.currentTarget.value || '', toFixed))
    },
    [toFixed, field],
  )

  const pattern = toFixed ? '[0-9\\-\\.]*' : '[0-9\\-]*'

  return (
    <FormControl
      label={label}
      disabled={disabled}
      caption={caption}
      feedback={
        !!fieldState.error && (
          <FieldMessage type="critical">
            {fieldState.error.message}
          </FieldMessage>
        )
      }
      htmlFor={name}
    >
      <Input
        inputMode={toFixed ? 'decimal' : 'numeric'}
        pattern={pattern}
        {...field}
        {...props}
        error={!!fieldState.error}
        onChange={onChange}
        value={field.value === null ? '' : field.value}
      />
    </FormControl>
  )
}
